import { useRef, useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, Instagram } from "lucide-react";

const instagramPosts = [
  { id: 1, imageUrl: "/assest/section-showcase/event_ent-9.jpg", postUrl: "https://www.instagram.com/immaculate_limited?igsh=NDB6amdwczE2MTRt&utm_source=qr" },
  { id: 2, imageUrl: "/assest/section-showcase/aviation-img1-10.jpg", postUrl: "https://www.instagram.com/immaculate_limited?igsh=NDB6amdwczE2MTRt&utm_source=qr" },
  { id: 3, imageUrl: "/assest/section-showcase/logistic-11.jpg", postUrl: "https://www.instagram.com/immaculate_limited?igsh=NDB6amdwczE2MTRt&utm_source=qr" },
  { id: 4, imageUrl: "/assest/section-showcase/retail-12.jpg", postUrl: "https://www.instagram.com/immaculate_limited?igsh=NDB6amdwczE2MTRt&utm_source=qr" },
  { id: 5, imageUrl: "/assest/section-showcase/architect-man-showing-something-project-his-colleague-foreman.jpg", postUrl: "https://www.instagram.com/immaculate_limited?igsh=NDB6amdwczE2MTRt&utm_source=qr" },
  { id: 6, imageUrl: "/assest/section-showcase/doct-14.jpg", postUrl: "https://www.instagram.com/immaculate_limited?igsh=NDB6amdwczE2MTRt&utm_source=qr" },
  { id: 7, imageUrl: "/assest/section-showcase/banking-15.jpg", postUrl: "https://www.instagram.com/immaculate_limited?igsh=NDB6amdwczE2MTRt&utm_source=qr" },
  { id: 8, imageUrl: "/assest/section-showcase/tech-16.jpg", postUrl: "https://www.instagram.com/immaculate_limited?igsh=NDB6amdwczE2MTRt&utm_source=qr" },
];

// A helper for hiding the scrollbar, assuming a global CSS class is available.
// .scrollbar-hide::-webkit-scrollbar { display: none; }
// .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }

export default function InstagramGallery() {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScrollability = useCallback(() => {
    const element = scrollContainerRef.current;
    if (element) {
      const hasOverflow = element.scrollWidth > element.clientWidth;
      
      setCanScrollLeft(element.scrollLeft > 0);
      setCanScrollRight(hasOverflow && element.scrollLeft < element.scrollWidth - element.clientWidth - 1);
    }
  }, []);

  useEffect(() => {
    const element = scrollContainerRef.current;
    if (element) {
      checkScrollability();
      element.addEventListener("scroll", checkScrollability);
      window.addEventListener("resize", checkScrollability);
    }

    return () => {
      if (element) {
        element.removeEventListener("scroll", checkScrollability);
        window.removeEventListener("resize", checkScrollability);
      }
    };
  }, [checkScrollability]);

  const scroll = (direction: "left" | "right") => {
    const element = scrollContainerRef.current;
    if (element) {
      const scrollAmount = element.clientWidth * 0.8;
      element.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  return (
    <section 
      className="py-12 sm:py-16 md:py-20 bg-[#e7f8ee]"
      style={{
        backgroundImage: "url('/assest/section-showcase/blog_bg-17.png')",
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div className="container mx-auto">
        <div className="flex justify-between items-center mb-8 sm:mb-10 px-5 sm:px-6 lg:px-[15px]">
          <h2 className="font-display text-gray-700 text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-normal leading-tight">
            # immaculate
          </h2>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => scroll("left")}
              disabled={!canScrollLeft}
              aria-label="Scroll left"
              className="w-11 h-11 sm:w-12 sm:h-12 rounded-full border-2 border-gray-300 flex items-center justify-center text-gray-600 transition-all hover:border-gray-400 hover:bg-gray-50 disabled:text-gray-300 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={() => scroll("right")}
              disabled={!canScrollRight}
              aria-label="Scroll right"
              className="w-11 h-11 sm:w-12 sm:h-12 rounded-full border-2 border-gray-300 flex items-center justify-center text-gray-600 transition-all hover:border-gray-400 hover:bg-gray-50 disabled:text-gray-300 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="relative">
          <div
            ref={scrollContainerRef}
            className="flex gap-0 overflow-x-auto scrollbar-hide"
          >
            {instagramPosts.map((post) => (
              <div key={post.id} className="flex-shrink-0">
                <a
                  href={post.postUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative block w-[260px] h-[220px] sm:w-[320px] sm:h-[260px] md:w-[380px] md:h-[300px] lg:w-[440px] lg:h-[340px] overflow-hidden"
                >
                  <img
                    src={post.imageUrl}
                    alt="Instagram post by IMMACULATE"
                    className="w-full h-full object-cover transition-all duration-500 ease-in-out group-hover:scale-110 group-hover:brightness-75"
                  />
                  <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm rounded-full p-2 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                    <Instagram className="h-6 w-6 sm:h-7 sm:w-7 text-gray-800" />
                  </div>
                </a>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}